import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel,
  Heading,
} from 'react-aria-components';
import styles from './Disclosure.module.css';

interface DisclosureProps {
  title: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Heading level for the trigger. */
  level?: 2 | 3 | 4;
  /** No top rule, for use inside a card that already separates its parts. */
  bare?: boolean;
}

/** One level of progressive disclosure: a <button aria-expanded> plus its panel. */
export function Disclosure({
  title,
  children,
  defaultExpanded = false,
  isExpanded,
  onExpandedChange,
  level = 4,
  bare = false,
}: DisclosureProps) {
  return (
    <AriaDisclosure
      className={styles.disclosure}
      data-bare={bare || undefined}
      defaultExpanded={defaultExpanded}
      {...(isExpanded === undefined ? {} : { isExpanded })}
      {...(onExpandedChange ? { onExpandedChange } : {})}
    >
      <Heading level={level} className={styles.heading}>
        <AriaButton slot="trigger" className={styles.trigger}>
          <ChevronRight size={18} aria-hidden="true" className={styles.chevron} />
          {title}
        </AriaButton>
      </Heading>
      <DisclosurePanel className={styles.panel}>{children}</DisclosurePanel>
    </AriaDisclosure>
  );
}
